import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const OtpModal = ({ open, onClose, onSendOtp, onVerifyOtp, phone, title, subtitle, recaptchaId = 'recaptcha-container' }) => {
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const inputsRef = useRef([]);
  const [isSending, setIsSending] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [confirmationResult, setConfirmationResult] = useState(null);
  const [counter, setCounter] = useState(30);

  useEffect(() => {
    if (!open) return;
    setCode(['', '', '', '', '', '']);
    setCounter(30);
    setIsSending(true);
    onSendOtp(phone, recaptchaId)
      .then((cr) => setConfirmationResult(cr))
      .finally(() => setIsSending(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (counter <= 0) return;
    const t = setTimeout(() => setCounter((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [counter, open]);

  const handleChange = (idx, val) => {
    if (!/^[0-9]?$/.test(val)) return;
    const next = [...code];
    next[idx] = val;
    setCode(next);
    if (val && idx < 5) inputsRef.current[idx + 1]?.focus();
  };

  const handleKeyDown = (idx, e) => {
    if (e.key === 'Backspace' && !code[idx] && idx > 0) {
      inputsRef.current[idx - 1]?.focus();
    }
  };

  const submit = async (e) => {
    e?.preventDefault();
    if (!confirmationResult) return;
    const otp = code.join('');
    if (otp.length !== 6) return;
    setIsVerifying(true);
    try {
      await onVerifyOtp(confirmationResult, otp);
      onClose?.();
    } finally {
      setIsVerifying(false);
    }
  };

  const resend = async () => {
    if (isSending) return;
    setCounter(30);
    setIsSending(true);
    try {
      const cr = await onSendOtp(phone, recaptchaId);
      setConfirmationResult(cr);
    } finally {
      setIsSending(false);
    }
  };

  return (
    <>
      <div id={recaptchaId} />
      <AnimatePresence>
        {open && (
          <motion.div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}>
            <motion.div className="bg-white w-full max-w-md mx-4 p-6 shadow-xl"
              initial={{ y: 40, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 40, opacity: 0 }}>
              <div className="mb-4">
                <h3 className="text-2xl font-serif text-deep-brown">{title || 'Verify OTP'}</h3>
                <p className="text-sm text-warm-gray mt-1">{subtitle || `We sent an OTP to ${phone}`}</p>
              </div>
              <form onSubmit={submit} className="space-y-4">
                <div className="flex gap-2 justify-between">
                  {code.map((d, idx) => (
                    <input
                      key={idx}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      className="w-12 h-12 border border-warm-gray/30 text-center text-lg"
                      value={d}
                      ref={(el) => (inputsRef.current[idx] = el)}
                      onChange={(e) => handleChange(idx, e.target.value)}
                      onKeyDown={(e) => handleKeyDown(idx, e)}
                    />
                  ))}
                </div>
                <button type="submit" className="w-full btn-primary disabled:opacity-50" disabled={isSending || isVerifying}>
                  {isVerifying ? 'Verifying...' : 'Verify'}
                </button>
              </form>
              <div className="mt-4 flex items-center justify-between text-sm text-warm-gray">
                <button className="hover:text-deep-brown" onClick={onClose}>Cancel</button>
                <button className="hover:text-deep-brown disabled:opacity-50" onClick={resend} disabled={counter > 0 || isSending}>
                  {isSending ? 'Sending...' : counter > 0 ? `Resend in ${counter}s` : 'Resend OTP'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default OtpModal;
